import { useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '../../hooks/useAuth'
import LoadingSpinner from '../../components/common/LoadingSpinner'

// Password requirements based on Cognito policy
const PASSWORD_REQUIREMENTS = 'Must be 8+ characters with uppercase, lowercase, and a number'

export default function AdminLogin() {
  const {
    login,
    completeNewPassword,
    initiatePasswordReset,
    completePasswordReset,
    cancelPasswordReset,
    isLoading,
    error: authError,
    needsNewPassword,
    needsPasswordReset,
    resetEmail,
  } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [verificationCode, setVerificationCode] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [showForgotPassword, setShowForgotPassword] = useState(false)

  const locationState = location.state as { from?: string; sessionExpired?: boolean } | null
  const from = locationState?.from || '/admin'
  const sessionExpired = locationState?.sessionExpired

  // Validate password meets Cognito requirements
  const validatePassword = (pwd: string): string | null => {
    if (pwd.length < 8) return 'Password must be at least 8 characters'
    if (!/[A-Z]/.test(pwd)) return 'Password must contain an uppercase letter'
    if (!/[a-z]/.test(pwd)) return 'Password must contain a lowercase letter'
    if (!/[0-9]/.test(pwd)) return 'Password must contain a number'
    return null
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setSubmitting(true)

    try {
      const result = await login(email, password)
      if (!result?.needsNewPassword && !result?.needsPasswordReset) {
        navigate(from, { replace: true })
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed')
    } finally {
      setSubmitting(false)
    }
  }

  const handleNewPassword = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match')
      return
    }

    const validationError = validatePassword(newPassword)
    if (validationError) {
      setError(validationError)
      return
    }

    setSubmitting(true)
    try {
      await completeNewPassword(newPassword)
      navigate(from, { replace: true })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to set new password')
    } finally {
      setSubmitting(false)
    }
  }

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    if (!email) {
      setError('Please enter your email address')
      return
    }

    setSubmitting(true)
    try {
      await initiatePasswordReset(email)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send reset code')
    } finally {
      setSubmitting(false)
    }
  }

  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match')
      return
    }

    const validationError = validatePassword(newPassword)
    if (validationError) {
      setError(validationError)
      return
    }

    if (!verificationCode) {
      setError('Please enter the verification code')
      return
    }

    setSubmitting(true)
    try {
      await completePasswordReset(verificationCode, newPassword)
      setShowForgotPassword(false)
      setNewPassword('')
      setConfirmPassword('')
      setVerificationCode('')
      setError(null)
      alert('Password reset successful! Please log in with your new password.')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reset password')
    } finally {
      setSubmitting(false)
    }
  }

  const handleCancelReset = () => {
    cancelPasswordReset()
    setShowForgotPassword(false)
    setNewPassword('')
    setConfirmPassword('')
    setVerificationCode('')
    setError(null)
  }

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col">
      <div className="flex-1 flex items-center justify-center px-4">
        <div className="max-w-md w-full">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-sla-blue">
              School Lunch Angels
            </h1>
            <p className="text-gray-600 mt-2">Admin Dashboard Login</p>
          </div>

          <div className="card">
            {/* Session expired notification */}
            {sessionExpired && !needsPasswordReset && !needsNewPassword && !showForgotPassword && (
              <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-3 rounded-lg text-sm mb-4">
                Your session has expired. Please log in again.
              </div>
            )}

            {needsPasswordReset ? (
              // Password Reset Form (with verification code)
              <form onSubmit={handlePasswordReset} className="space-y-4">
                <div className="bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded-lg text-sm mb-4">
                  A verification code has been sent to {resetEmail}. Enter the code and your new password below.
                </div>

                <div>
                  <label className="label">Verification Code</label>
                  <input
                    type="text"
                    className="input"
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value)}
                    required
                    autoComplete="one-time-code"
                    placeholder="Enter code from email"
                  />
                </div>

                <div>
                  <label className="label">New Password</label>
                  <input
                    type="password"
                    className="input"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    minLength={8}
                    autoComplete="new-password"
                  />
                  <p className="text-xs text-gray-500 mt-1">{PASSWORD_REQUIREMENTS}</p>
                </div>

                <div>
                  <label className="label">Confirm New Password</label>
                  <input
                    type="password"
                    className="input"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    minLength={8}
                    autoComplete="new-password"
                  />
                </div>

                {(error || authError) && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                    {error || authError}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={submitting || isLoading}
                  className="btn btn-primary w-full"
                >
                  {submitting ? (
                    <span className="flex items-center justify-center gap-2">
                      <LoadingSpinner size="sm" />
                      Resetting password...
                    </span>
                  ) : (
                    'Reset Password'
                  )}
                </button>

                <button
                  type="button"
                  onClick={handleCancelReset}
                  className="btn btn-secondary w-full"
                >
                  Cancel
                </button>
              </form>
            ) : needsNewPassword ? (
              // New Password Form (for first-time users)
              <form onSubmit={handleNewPassword} className="space-y-4">
                <div className="bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded-lg text-sm mb-4">
                  Please set a new password to continue.
                </div>

                <div>
                  <label className="label">New Password</label>
                  <input
                    type="password"
                    className="input"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    minLength={8}
                    autoComplete="new-password"
                  />
                  <p className="text-xs text-gray-500 mt-1">{PASSWORD_REQUIREMENTS}</p>
                </div>

                <div>
                  <label className="label">Confirm New Password</label>
                  <input
                    type="password"
                    className="input"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    minLength={8}
                    autoComplete="new-password"
                  />
                </div>

                {(error || authError) && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                    {error || authError}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={submitting || isLoading}
                  className="btn btn-primary w-full"
                >
                  {submitting ? (
                    <span className="flex items-center justify-center gap-2">
                      <LoadingSpinner size="sm" />
                      Setting password...
                    </span>
                  ) : (
                    'Set New Password'
                  )}
                </button>
              </form>
            ) : showForgotPassword ? (
              // Forgot Password Form
              <form onSubmit={handleForgotPassword} className="space-y-4">
                <div className="bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded-lg text-sm mb-4">
                  Enter your email address and we'll send you a verification code to reset your password.
                </div>

                <div>
                  <label className="label">Email</label>
                  <input
                    type="email"
                    className="input"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoComplete="email"
                  />
                </div>

                {(error || authError) && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                    {error || authError}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={submitting || isLoading}
                  className="btn btn-primary w-full"
                >
                  {submitting ? (
                    <span className="flex items-center justify-center gap-2">
                      <LoadingSpinner size="sm" />
                      Sending code...
                    </span>
                  ) : (
                    'Send Verification Code'
                  )}
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setShowForgotPassword(false)
                    setError(null)
                  }}
                  className="btn btn-secondary w-full"
                >
                  Back to Login
                </button>
              </form>
            ) : (
              // Login Form
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="label">Email</label>
                  <input
                    type="email"
                    className="input"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoComplete="email"
                  />
                </div>

                <div>
                  <label className="label">Password</label>
                  <input
                    type="password"
                    className="input"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    autoComplete="current-password"
                  />
                </div>

                {(error || authError) && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                    {error || authError}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={submitting || isLoading}
                  className="btn btn-primary w-full"
                >
                  {submitting ? (
                    <span className="flex items-center justify-center gap-2">
                      <LoadingSpinner size="sm" />
                      Signing in...
                    </span>
                  ) : (
                    'Sign In'
                  )}
                </button>

                <div className="text-center">
                  <button
                    type="button"
                    onClick={() => setShowForgotPassword(true)}
                    className="text-sm text-sla-blue hover:underline"
                  >
                    Forgot password?
                  </button>
                </div>
              </form>
            )}

            <div className="mt-6 pt-6 border-t text-center text-sm text-gray-500">
              <p>
                Contact{' '}
                <a
                  href="mailto:secretary@schoollunchangels.org"
                  className="text-sla-blue hover:underline"
                >
                  secretary@schoollunchangels.org
                </a>{' '}
                if you need access.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
